import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Drawer, Input } from 'antd';
import { Link } from 'react-router-dom';
import { debounce } from 'lodash';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import {
    DataTable,
    FilterPills,
    PageCard,
    PageShell,
    PageToolbar,
    StatCard,
    StatusBadge,
} from '../../components/ui';
import PatientHistoryPanel from '../../components/PatientHistoryPanel';
import {
    ACTIVITY_PAGE_SIZE,
    ACTIVITY_STATUSES,
    fetchPatientActivity,
    fetchPatientActivitySummary,
    firstApiError,
} from '../../services/patientActivityService';

const STATUS_TONES = {
    overdue: 'red',
    recent: 'green',
    never_visited: 'slate',
    visited: 'brand',
};

function formatDay(value, dash) {
    if (!value) return dash;
    const parsed = dayjs(value);
    return parsed.isValid() ? parsed.format('DD.MM.YYYY') : value;
}

export default function PatientActivity() {
    const { t } = useTranslation();
    const dash = t('common.dash');
    const [status, setStatus] = useState('overdue');
    const [searchInput, setSearchInput] = useState('');
    const [search, setSearch] = useState('');
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: ACTIVITY_PAGE_SIZE,
        total: 0,
    });
    const [summary, setSummary] = useState({
        total: 0,
        overdue: 0,
        recent: 0,
        never_visited: 0,
        visited: 0,
        referenceDate: null,
        cutoffDate: null,
    });
    const [historyPatient, setHistoryPatient] = useState(null);

    const applySearch = useMemo(
        () =>
            debounce((value) => {
                setSearch(value.trim().slice(0, 100));
                setPagination((prev) => ({ ...prev, current: 1 }));
            }, 400),
        []
    );

    useEffect(() => () => applySearch.cancel(), [applySearch]);

    const loadSummary = useCallback(async (query) => {
        try {
            const result = await fetchPatientActivitySummary(query);
            setSummary(result);
        } catch (error) {
            const code = error?.response?.status;
            if (code === 401) return;
            if (code === 403) toast.error(t('patientActivity.forbidden'));
            else toast.error(firstApiError(error) || t('patientActivity.loadError'));
        }
    }, [t]);

    const loadList = useCallback(async () => {
        setLoading(true);
        try {
            const result = await fetchPatientActivity({
                status,
                search,
                page: pagination.current,
                perPage: pagination.pageSize,
            });
            setItems(result.items);
            setPagination((prev) => ({
                ...prev,
                current: result.pagination.current,
                pageSize: result.pagination.pageSize,
                total: result.pagination.total,
            }));
            if (result.referenceDate || result.cutoffDate) {
                setSummary((prev) => ({
                    ...prev,
                    referenceDate: result.referenceDate ?? prev.referenceDate,
                    cutoffDate: result.cutoffDate ?? prev.cutoffDate,
                }));
            }
        } catch (error) {
            const code = error?.response?.status;
            if (code === 401) return;
            if (code === 403) toast.error(t('patientActivity.forbidden'));
            else toast.error(firstApiError(error) || t('patientActivity.loadError'));
        } finally {
            setLoading(false);
        }
    }, [status, search, pagination.current, pagination.pageSize, t]);

    useEffect(() => {
        loadSummary(search);
    }, [search, loadSummary]);

    useEffect(() => {
        loadList();
    }, [loadList]);

    const handleTabChange = (next) => {
        setStatus(next);
        setPagination((prev) => ({ ...prev, current: 1 }));
    };

    const columns = [
        {
            title: t('common.name'),
            dataIndex: 'name',
            key: 'name',
            render: (name, record) => (
                <Link to={`/patient/${record.id}`} className="text-sm font-medium text-[#1D2C7B] hover:underline">
                    {name || dash}
                </Link>
            ),
        },
        {
            title: t('common.code'),
            dataIndex: 'code',
            key: 'code',
            width: 140,
            render: (code) => <span className="text-sm tabular-nums text-slate-600">{code || dash}</span>,
        },
        {
            title: t('common.phone'),
            dataIndex: 'phone',
            key: 'phone',
            width: 160,
            render: (phone) =>
                phone ? (
                    <a href={`tel:+${String(phone).replace(/^\+/, '')}`} className="text-sm tabular-nums text-[#1D2C7B]">
                        {phone}
                    </a>
                ) : (
                    <span className="text-sm text-slate-400">{dash}</span>
                ),
        },
        {
            title: t('patientActivity.lastVisit'),
            dataIndex: 'last_visited_at',
            key: 'last_visited_at',
            width: 160,
            render: (value) => (
                <span className="text-sm tabular-nums text-slate-700">
                    {value ? formatDay(value, dash) : t('patientActivity.neverVisitedLabel')}
                </span>
            ),
        },
        {
            title: t('patientActivity.visitsCount'),
            dataIndex: 'visits_count',
            key: 'visits_count',
            width: 110,
            render: (count) => <span className="text-sm tabular-nums text-slate-700">{count ?? 0}</span>,
        },
        {
            title: t('common.status'),
            dataIndex: 'activity_status',
            key: 'activity_status',
            width: 180,
            render: (value) => (
                <StatusBadge tone={STATUS_TONES[value] ?? 'muted'}>
                    {t(`patientActivity.${value}`, { defaultValue: value })}
                </StatusBadge>
            ),
        },
        {
            title: t('patientActivity.createdAt'),
            dataIndex: 'created_at',
            key: 'created_at',
            width: 130,
            render: (value) => <span className="text-sm tabular-nums text-slate-600">{formatDay(value, dash)}</span>,
        },
        {
            title: t('common.details'),
            key: 'history',
            width: 110,
            render: (_, record) => (
                <button
                    type="button"
                    className="text-xs font-semibold text-[#1D2C7B] hover:underline"
                    onClick={() => setHistoryPatient(record)}
                >
                    {t('patientActivity.history')}
                </button>
            ),
        },
    ];

    const renderMobileCard = (record) => (
        <div>
            <div className="flex items-start gap-2">
                <div className="min-w-0 flex-1">
                    <Link to={`/patient/${record.id}`} className="block truncate text-sm font-semibold text-[#1D2C7B]">
                        {record.name || dash}
                    </Link>
                    <span className="mt-0.5 block text-xs tabular-nums text-slate-500">{record.phone || dash}</span>
                </div>
                <StatusBadge tone={STATUS_TONES[record.activity_status] ?? 'muted'}>
                    {t(`patientActivity.${record.activity_status}`, { defaultValue: record.activity_status })}
                </StatusBadge>
            </div>
            <div className="mt-2.5 flex flex-wrap items-center gap-x-3 gap-y-1.5 text-xs text-slate-600">
                <span className="tabular-nums">{record.code || dash}</span>
                <span>
                    {record.last_visited_at
                        ? formatDay(record.last_visited_at, dash)
                        : t('patientActivity.neverVisitedLabel')}
                </span>
                <span>
                    {t('patientActivity.visitsCount')}: {record.visits_count ?? 0}
                </span>
            </div>
            <button
                type="button"
                className="mt-2 text-xs font-semibold text-[#1D2C7B]"
                onClick={() => setHistoryPatient(record)}
            >
                {t('patientActivity.history')}
            </button>
        </div>
    );

    return (
        <PageShell>
            <div className="grid grid-cols-2 gap-2 md:grid-cols-4 md:gap-3">
                <StatCard tone="red" count={summary.overdue} label={t('patientActivity.overdue')} hint={t('patientActivity.overdueHint')} />
                <StatCard tone="green" count={summary.recent} label={t('patientActivity.recent')} hint={t('patientActivity.recentHint')} />
                <StatCard tone="slate" count={summary.never_visited} label={t('patientActivity.never_visited')} hint={t('patientActivity.neverHint')} />
                <StatCard tone="brand" count={summary.visited} label={t('patientActivity.visited')} hint={t('patientActivity.visitedHint')} />
            </div>

            <PageCard>
                <PageToolbar
                    title={t('patientActivity.title')}
                    count={pagination.total}
                    filters={
                        <Input
                            allowClear
                            size="small"
                            maxLength={100}
                            prefix={<MagnifyingGlassIcon className="h-4 w-4 text-slate-400" />}
                            placeholder={t('patientActivity.search')}
                            value={searchInput}
                            onChange={(e) => {
                                setSearchInput(e.target.value);
                                applySearch(e.target.value);
                            }}
                            className="md:!w-[260px]"
                        />
                    }
                />

                <FilterPills
                    value={status}
                    onChange={handleTabChange}
                    options={ACTIVITY_STATUSES.map((item) => ({
                        value: item,
                        label: t(`patientActivity.${item}`),
                        count: summary[item],
                    }))}
                />

                {(summary.referenceDate || summary.cutoffDate) && (
                    <p className="mb-0 px-3 pb-2 text-xs text-slate-400">
                        {t('patientActivity.cutoffNote', {
                            today: formatDay(summary.referenceDate, dash),
                            cutoff: formatDay(summary.cutoffDate, dash),
                        })}
                    </p>
                )}

                <DataTable
                    rowKey="id"
                    columns={columns}
                    dataSource={items}
                    loading={loading}
                    mobileCard={renderMobileCard}
                    pagination={{
                        ...pagination,
                        showSizeChanger: false,
                        position: ['bottomCenter'],
                        className: 'px-4 pb-3',
                        showTotal: (total, range) => (
                            <span className="text-xs text-slate-500">
                                {t('common.perPage', { from: range[0], to: range[1], total })}
                            </span>
                        ),
                    }}
                    onChange={(next) => setPagination((prev) => ({ ...prev, current: next.current, pageSize: next.pageSize }))}
                    scrollX={1100}
                />
            </PageCard>

            <Drawer
                title={historyPatient ? historyPatient.name : t('patientActivity.history')}
                open={Boolean(historyPatient)}
                onClose={() => setHistoryPatient(null)}
                width={Math.min(720, typeof window !== 'undefined' ? window.innerWidth : 720)}
                destroyOnClose
            >
                {historyPatient ? <PatientHistoryPanel patientId={historyPatient.id} /> : null}
            </Drawer>
        </PageShell>
    );
}
